import { Dispatch } from "react"
import { assetAction, assetReducer } from "./assets"
import { libraryAction, libraryReducer } from "./library"
import { blockInteractionAction } from "./block_interactions"
import { blockTextAction, blockTextReducer } from "./block_texts"
import { blockVideoAction, blockVideoReducer } from "./block_videos"
import {
  blockPresentationAction,
  blockPresentationReducer
} from "./block_presentations"
import { blockImageAction, blockImageReducer } from "./block_images"
import { blockPdfAction, blockPdfReducer } from "./block_pdfs"
import { blockEmptyAction, blockEmptyReducer } from "./block_empty"
import {
  elementCourseStateAction,
  elementCourseStateReducer
} from "./element_course_state"
import { seqAssessmentAction, seqAssessmentReducer } from "./seq_assessments"
import { seqConfigAction, seqConfigReducer } from "./seq_configs"
import { seqExerciseAction, seqExerciseReducer } from "./seq_exercises"
import { seqLogicAction, seqLogicReducer } from "./seq_logics"
import { seqScreenAction, seqScreenReducer } from "./seq_screens"
import { snapCourseAction, snapCourseReducer } from "./snap_courses"
import { snapAction, snapReducer } from "./snaps"
import { snapShotAction, snapShotReducer } from "./snap_shots"
import { userAction, userReducer } from "./users"
import { snapStateAction, snapStateReducer } from "./snap_state"
import { wordBankAction, wordBankReducer } from "./word_banks"
import { blockQtiInteractionAction } from "./block_qti_interactions"
import { seqQtiScreenAction } from "./seq_qti_screens"
import { seqPassageAction } from "./seq_passage"

export type { Asset } from "./assets"
export { assetAction, assetReducer } from "./assets"

export type { Media } from "./library"
export { libraryAction, libraryReducer } from "./library"

export type { BlockInteraction } from "./block_interactions"

export type { BlockQtiInteraction } from "./block_qti_interactions"
export {
  blockQtiInteractionAction,
  blockQtiInteractionReducer
} from "./block_qti_interactions"

export {
  blockInteractionAction,
  blockInteractionReducer
} from "./block_interactions"

export type { BlockText } from "./block_texts"
export { blockTextAction, blockTextReducer } from "./block_texts"

export type { BlockVideo } from "./block_videos"
export { blockVideoAction, blockVideoReducer } from "./block_videos"

export type { BlockPresentation } from "./block_presentations"
export {
  blockPresentationAction,
  blockPresentationReducer
} from "./block_presentations"

export type { BlockImage } from "./block_images"
export { blockImageAction, blockImageReducer } from "./block_images"

export type { BlockPdf } from "./block_pdfs"
export { blockPdfAction, blockPdfReducer } from "./block_pdfs"

export type { BlockEmpty } from "./block_empty"
export { blockEmptyAction, blockEmptyReducer } from "./block_empty"

export type { ElementCourseState } from "./element_course_state"
export {
  elementCourseStateAction,
  elementCourseStateReducer
} from "./element_course_state"

export type { SeqAssessment } from "./seq_assessments"
export { seqAssessmentAction, seqAssessmentReducer } from "./seq_assessments"

export type { SeqConfig } from "./seq_configs"
export { seqConfigAction, seqConfigReducer } from "./seq_configs"

export type { SeqExercise } from "./seq_exercises"
export { seqExerciseAction, seqExerciseReducer } from "./seq_exercises"

export type { SeqLogic } from "./seq_logics"
export { seqLogicAction, seqLogicReducer } from "./seq_logics"

export type { SeqScreen } from "./seq_screens"
export { seqScreenAction, seqScreenReducer } from "./seq_screens"

export type { SeqQtiScreen } from "./seq_qti_screens"
export { seqQtiScreenAction, seqQtiScreenReducer } from "./seq_qti_screens"

export type { SeqPassage } from "./seq_passage"
export { seqPassageAction, seqPassageReducer } from "./seq_passage"

export type { SnapCourse } from "./snap_courses"
export { snapCourseAction, snapCourseReducer } from "./snap_courses"

export type { Snap } from "./snaps"
export { snapAction, snapReducer } from "./snaps"

export type { SnapShot } from "./snap_shots"
export { snapShotAction, snapShotReducer } from "./snap_shots"

export type { User } from "./users"
export { userAction, userReducer } from "./users"

export type { SnapState } from "./snap_state"
export { snapStateAction, snapStateReducer } from "./snap_state"

export type { WordBank } from "./word_banks"
export { wordBankAction, wordBankReducer } from "./word_banks"

export const actions = {
  assets: assetAction,
  library: libraryAction,
  block_interactions: blockInteractionAction,
  block_qti_interactions: blockQtiInteractionAction,
  block_presentations: blockPresentationAction,
  block_texts: blockTextAction,
  block_videos: blockVideoAction,
  block_images: blockImageAction,
  block_pdfs: blockPdfAction,
  block_empty: blockEmptyAction,
  element_course_state: elementCourseStateAction,
  seq_assessments: seqAssessmentAction,
  seq_configs: seqConfigAction,
  seq_exercises: seqExerciseAction,
  seq_logics: seqLogicAction,
  seq_screens: seqScreenAction,
  seq_qti_screens: seqQtiScreenAction,
  seq_passages: seqPassageAction,
  snap_courses: snapCourseAction,
  snaps: snapAction,
  snap_shots: snapShotAction,
  snap_state: snapStateAction,
  users: userAction,
  word_banks: wordBankAction
}

type AnyFunction = (...args: any[]) => any
type ReplaceReturnType<T extends (...a: any) => any, TNewReturn> = (
  ...a: Parameters<T>
) => TNewReturn

export type CollectionActions = typeof actions
export type CollectionDispatchedActions<T extends keyof CollectionActions> = {
  [key in keyof CollectionActions[T]]: ReplaceReturnType<
    CollectionActions[T][key] extends AnyFunction
      ? (typeof actions)[T][key]
      : never,
    void
  >
}

export function dispatchWrapperForActionsOfCollection<
  K extends keyof CollectionActions
>(collection: K, dispatch: Dispatch<any>) {
  const wrap = <Func extends AnyFunction>(
    fn: Func
  ): ((...args: Parameters<Func>) => void) => {
    const wrappedFn = (...args: Parameters<Func>): void => {
      return dispatch(fn(...args))
    }
    return wrappedFn
  }

  const wrappedActions = Object.fromEntries(
    Object.entries(actions[collection]).map(k => [
      k[0],
      wrap<(typeof k)[1]>(k[1])
    ])
  ) as unknown as CollectionDispatchedActions<K>

  return wrappedActions
}
